import { useEffect, useState } from "react";
import { X, Download, Smartphone, Share } from "lucide-react";

// Stato globale condiviso tra il banner automatico e il bottone flottante
let globalDeferredPrompt = null;
const listeners = new Set();
if (typeof window !== "undefined") {
    window.addEventListener("beforeinstallprompt", (e) => {
        e.preventDefault();
        globalDeferredPrompt = e;
        listeners.forEach((fn) => fn(e));
    });
    window.addEventListener("appinstalled", () => {
        globalDeferredPrompt = null;
        listeners.forEach((fn) => fn(null));
    });
}

const isStandalone = () =>
    (typeof window !== "undefined") &&
    (window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true);

const detectIOS = () => {
    if (typeof window === "undefined") return false;
    return /iPhone|iPad|iPod/.test(window.navigator.userAgent) && !window.MSStream;
};

/** Prompt automatico non invasivo (banner in basso, appare dopo alcuni secondi). */
export function InstallPWA() {
    const [deferredPrompt, setDeferredPrompt] = useState(globalDeferredPrompt);
    const [visible, setVisible] = useState(false);
    const [isIOS, setIsIOS] = useState(false);

    useEffect(() => {
        if (isStandalone()) return;
        const dismissed = localStorage.getItem("velora_pwa_dismissed_at");
        if (dismissed) {
            const days = (Date.now() - parseInt(dismissed, 10)) / (1000 * 60 * 60 * 24);
            if (days < 30) return;
        }
        const iOS = detectIOS();
        setIsIOS(iOS);
        const onChange = (e) => { setDeferredPrompt(e); if (e) setTimeout(() => setVisible(true), 8000); };
        listeners.add(onChange);
        if (globalDeferredPrompt) setTimeout(() => setVisible(true), 8000);
        if (iOS) setTimeout(() => setVisible(true), 15000);
        return () => listeners.delete(onChange);
    }, []);

    const install = async () => {
        if (!deferredPrompt) return;
        deferredPrompt.prompt();
        try { await deferredPrompt.userChoice; } catch { /* ignore */ }
        globalDeferredPrompt = null;
        setDeferredPrompt(null); setVisible(false);
    };
    const dismiss = () => { localStorage.setItem("velora_pwa_dismissed_at", String(Date.now())); setVisible(false); };

    if (!visible) return null;
    return (
        <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:max-w-sm z-40 bg-[#15181C] border-2 border-[#E2231A] shadow-2xl" data-testid="pwa-install-banner">
            <div className="p-4">
                <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#15181C] border border-[#E2231A] flex items-center justify-center flex-shrink-0">
                            <div className="w-5 h-5 rounded-full bg-[#E2231A]" />
                        </div>
                        <div>
                            <div className="font-display text-lg uppercase tracking-widest text-[#F4F2ED]">Installa VELORA JDM</div>
                            <div className="text-[10px] text-[#9AA2AD] uppercase tracking-widest">Accesso rapido dall'home</div>
                        </div>
                    </div>
                    <button data-testid="pwa-dismiss-btn" onClick={dismiss} className="text-[#9AA2AD] hover:text-[#F4F2ED] p-1 -mt-1 -mr-1" aria-label="Chiudi"><X size={16} /></button>
                </div>
                {isIOS ? (
                    <div className="mt-3 text-xs text-[#9AA2AD] leading-relaxed">
                        Su iPhone: tocca <b className="text-[#F4F2ED]">Condividi</b> in basso, poi <b className="text-[#F4F2ED]">"Aggiungi a Home"</b>.
                    </div>
                ) : deferredPrompt ? (
                    <button data-testid="pwa-install-btn" onClick={install} className="mt-3 w-full bg-[#E2231A] hover:bg-[#C11B15] text-[#F4F2ED] px-4 py-2 uppercase tracking-widest text-xs font-display flex items-center justify-center gap-2 transition-colors duration-150">
                        <Download size={14} /> Installa
                    </button>
                ) : (
                    <div className="mt-3 text-xs text-[#9AA2AD]">Apri il menu del browser e scegli <b className="text-[#F4F2ED]">"Aggiungi a Home"</b>.</div>
                )}
            </div>
        </div>
    );
}

/** Bottone/chip permanente "Scarica App" — sempre visibile, apre modale con istruzioni o prompt. */
export function InstallAppButton({ variant = "chip" }) {
    const [deferredPrompt, setDeferredPrompt] = useState(globalDeferredPrompt);
    const [installed, setInstalled] = useState(isStandalone());
    const [modalOpen, setModalOpen] = useState(false);
    const [isIOS, setIsIOS] = useState(false);

    useEffect(() => {
        setIsIOS(detectIOS());
        const onChange = (e) => { setDeferredPrompt(e); if (e === null) setInstalled(true); };
        listeners.add(onChange);
        return () => listeners.delete(onChange);
    }, []);

    if (installed) return null;

    const click = async () => {
        if (deferredPrompt) {
            deferredPrompt.prompt();
            try {
                const res = await deferredPrompt.userChoice;
                if (res?.outcome === "accepted") setInstalled(true);
            } catch { /* ignore */ }
            globalDeferredPrompt = null;
            setDeferredPrompt(null);
            return;
        }
        // Nessun prompt disponibile (iOS o browser sconosciuto) → mostra istruzioni
        setModalOpen(true);
    };

    const chipClasses = variant === "chip"
        ? "fixed bottom-20 left-4 md:bottom-6 md:left-6 z-30 bg-[#15181C] border-2 border-[#E2231A] text-[#F4F2ED] px-3 py-2.5 flex items-center gap-2 text-[10px] uppercase tracking-widest shadow-2xl hover:bg-[#E2231A] transition-colors duration-150"
        : "border-2 border-[#E2231A] hover:bg-[#E2231A] hover:text-[#F4F2ED] text-[#F4F2ED] px-5 py-3 flex items-center gap-2 text-xs uppercase tracking-widest font-display transition-colors duration-150";

    return (
        <>
            <button
                data-testid="install-app-button"
                onClick={click}
                className={chipClasses}
                title="Installa VELORA JDM come app sul telefono"
                aria-label="Scarica app"
            >
                <Smartphone size={variant === "chip" ? 14 : 18} />
                <span>Scarica App</span>
            </button>

            {modalOpen && (
                <div className="fixed inset-0 z-50 bg-[#15181C]/90 backdrop-blur flex items-center justify-center p-4" data-testid="install-modal" onClick={() => setModalOpen(false)}>
                    <div className="max-w-md w-full bg-[#1C2026] border-2 border-[#E2231A]" onClick={e => e.stopPropagation()}>
                        <div className="border-b border-[#9AA2AD]/20 px-5 py-4 flex items-center justify-between">
                            <div className="font-display text-xl uppercase tracking-widest">Installa VELORA JDM</div>
                            <button onClick={() => setModalOpen(false)} data-testid="install-modal-close" className="text-[#9AA2AD] hover:text-[#F4F2ED]"><X size={20} /></button>
                        </div>
                        <div className="p-5 space-y-4">
                            {isIOS ? (
                                <>
                                    <div className="text-sm text-[#F4F2ED]">Su <b>iPhone / iPad</b> con Safari:</div>
                                    <ol className="space-y-3 text-sm text-[#F4F2ED]/90">
                                        <li className="flex gap-3"><span className="font-display text-2xl text-[#E2231A] flex-shrink-0">1</span><span>Tocca l'icona <Share size={14} className="inline text-[#4B7BEA]" /> <b>Condividi</b> in basso al centro</span></li>
                                        <li className="flex gap-3"><span className="font-display text-2xl text-[#E2231A] flex-shrink-0">2</span><span>Scorri verso il basso, tocca <b>"Aggiungi a Home"</b></span></li>
                                        <li className="flex gap-3"><span className="font-display text-2xl text-[#E2231A] flex-shrink-0">3</span><span>Tocca <b>"Aggiungi"</b> in alto a destra — l'icona apparirà sull'home</span></li>
                                    </ol>
                                    <div className="text-[10px] uppercase tracking-widest text-[#9AA2AD] pt-3 border-t border-[#9AA2AD]/10">Se stai usando Chrome iOS, apri prima questo sito in Safari.</div>
                                </>
                            ) : (
                                <>
                                    <div className="text-sm text-[#F4F2ED]">Su <b>Android</b>:</div>
                                    <ol className="space-y-3 text-sm text-[#F4F2ED]/90">
                                        <li className="flex gap-3"><span className="font-display text-2xl text-[#E2231A] flex-shrink-0">1</span><span>Tocca il menu <b>⋮</b> in alto a destra del browser</span></li>
                                        <li className="flex gap-3"><span className="font-display text-2xl text-[#E2231A] flex-shrink-0">2</span><span>Seleziona <b>"Installa app"</b> o <b>"Aggiungi a schermata Home"</b></span></li>
                                        <li className="flex gap-3"><span className="font-display text-2xl text-[#E2231A] flex-shrink-0">3</span><span>Conferma — l'icona apparirà nella tua schermata Home</span></li>
                                    </ol>
                                    <div className="text-[10px] uppercase tracking-widest text-[#9AA2AD] pt-3 border-t border-[#9AA2AD]/10">Funziona con Chrome, Edge, Samsung Internet, Brave.</div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
